<script>
const moment = require('moment-timezone')
const Vue = require('vue')
const { domain } = require('./Endpoints.vue').default
const DEFAULT_ACCOUNT_TIMEZONE = 'America/Sao_Paulo'
const TIMEZONE_ALIASES_TO_UTC = new Set([
    'UCT',
    'ETC/UCT',
    'ETC/UTC',
    'ETC/UNIVERSAL',
    'UNIVERSAL',
    'ZULU',
    'ETC/ZULU',
    'GREENWICH',
    'ETC/GREENWICH',
    'GMT0',
])

const normalizeTimezoneAlias = timezone => {
    if (typeof timezone !== 'string') {
        return null
    }

    const normalized = timezone.trim()

    if (!normalized) {
        return null
    }

    if (TIMEZONE_ALIASES_TO_UTC.has(normalized.toUpperCase())) {
        return DEFAULT_ACCOUNT_TIMEZONE
    }

    return normalized
}

const normalizeTimezoneId = timezone => {
    const normalized = normalizeTimezoneAlias(timezone)

    if (!normalized) {
        return null
    }

    if (moment.tz.zone(normalized)) {
        return normalized
    }

    return null
}

const resolveTimezoneId = timezone => {
    return normalizeTimezoneId(timezone) || DEFAULT_ACCOUNT_TIMEZONE
}

const formatGMTOffset = offsetInMinutes => {
    const sign = offsetInMinutes >= 0 ? '+' : '-'
    const absoluteOffset = Math.abs(offsetInMinutes)
    const hours = Math.floor(absoluteOffset / 60)
    const minutes = absoluteOffset % 60
    const formattedHours = hours.toString().padStart(2, '0')
    const formattedMinutes = minutes.toString().padStart(2, '0')
    return `GMT${sign}${formattedHours}:${formattedMinutes}`
}

const formatTimezoneText = timezone => {
    return timezone
        .split('/')
        .map(part => part.replace(/_/g, ' '))
        .join(' / ')
}

const buildTimezoneOption = timezone => {
    const normalized = normalizeTimezoneId(timezone)

    if (!normalized) {
        return null
    }

    const offsetInMinutes = moment().tz(normalized).utcOffset()
    const readableTimezone = formatTimezoneText(normalized)

    return {
        _id: normalized,
        name: `(${formatGMTOffset(offsetInMinutes)}) ${readableTimezone}`,
        offsetInMinutes,
    }
}

const resolveAccountTimezone = () => {
    try {
        const session = Vue?.prototype?.$session
        if (!session || !session.exists('user')) {
            return DEFAULT_ACCOUNT_TIMEZONE
        }

        const user = session.get('user')
        return resolveTimezoneId(user?.accountTimezone || user?.account?.timezone)
    } catch {
        return DEFAULT_ACCOUNT_TIMEZONE
    }
}

export default {
    camelize: text => {
        if (text) {
            const words = text.split(' ')
            const result = []
            words.slice(0).forEach(word => result.push(`${word.slice(0, 1).toUpperCase()}${word.slice(1).toLowerCase()}`))
            return result.join(' ')
        }
        return ''
    },
    toUpperCase: text => {
        if (text) {
            return text.toString().toUpperCase()
        }
        return ''
    },
    filterInvalidChars(field) {
        const forbiddenChars = /[\\\][;'`|,/{}`:?!+()*&%$#]/g
        this.data[field] = this.data[field]?.replace(forbiddenChars, '')
    },
    formatDecimal: text => {
        return parseFloat(Math.round(text * 100) / 100)
            .toFixed(2)
            .toString()
            .split(',')
            .join('')
            .split('.')
            .join(',')
    },
    sortAccountFn(x, y, col, rowX, rowY) {
        let nameX = rowX.account ? rowX.account.name : ''
        let nameY = rowY.account ? rowY.account.name : ''

        return nameX.localeCompare(nameY)
    },
    sortClientFn(x, y, col, rowX, rowY) {
        let nameX = rowX.client ? rowX.client.name : ''
        let nameY = rowY.client ? rowY.client.name : ''

        return nameX.localeCompare(nameY)
    },
    sortSiteFn(x, y, col, rowX, rowY) {
        let nameX = rowX.site ? rowX.site.name : ''
        let nameY = rowY.site ? rowY.site.name : ''

        return nameX.localeCompare(nameY)
    },
    sortAddressFn(x, y, col, rowX, rowY) {
        let nameX = rowX.address ? rowX.address?.address : ''
        let nameY = rowY.address ? rowY.address?.address : ''

        return nameX.localeCompare(nameY)
    },
    formatDate: text => {
        if (!text) return ''
        return moment.tz(text, resolveAccountTimezone()).format('DD/MM/YYYY')
    },
    getLocale: () => {
        return localStorage.getItem('locale') || 'pt-br'
    },
    formatDateTime: text => {
        const dateTimezone = moment.tz(text, resolveAccountTimezone())
        const formattedDate = dateTimezone.format('DD/MM/YYYY')
        const formattedTime = dateTimezone.format('HH:mm')

        return formattedDate + ' - ' + formattedTime
    },
    compareObjectFields(a, b) {
        if (a.name < b.name) return -1
        if (a.name > b.name) return 1
        return 0
    },
    concatAddress: addressItem => {
        if (!addressItem) return ''

        const { address, number, complement, neighborhood, city, state, cep } = addressItem

        let addressParts = []

        if (address) addressParts.push(address)
        if (number) addressParts.push(number)
        if (complement) addressParts.push(complement)
        if (neighborhood) addressParts.push(neighborhood)
        if (city) addressParts.push(city)
        if (state) addressParts.push(state)
        if (cep) addressParts.push(cep)

        return addressParts.join(' - ')
    },
    checkValidDate: function (date) {
        const currentDate = moment().format('DD/MM/YYYY')
        return moment(date).isSameOrAfter(currentDate)
    },
    compareDates: function (d1, d2) {
        let date1 = new Date(d1).getTime()
        let date2 = new Date(d2).getTime()

        if (date1 < date2) {
            return false
        } else if (date1 > date2) {
            return true
        } else {
            return true
        }
    },
    checkPassword: str => {
        var re = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,}$/
        return re.test(str)
    },
    isValidEmail: email => {
        var res = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/
        return res.test(email)
    },
    show(vue, group, type, title) {
        const date = moment.tz(resolveAccountTimezone()).format('DD/MM/YYYY - HH:mm:ss')
        const text = `${vue.$t('str.date.subtitle')}: ${date}`
        vue.$notify({
            group,
            title,
            text: text,
            type,
        })
    },
    isSuperAdminMaster: async state => {
        return state.$session.get('user')?.companyUser?.subtype === 'SUPER_ADMIN_MASTER' ? true : false
    },
    isAdmin: state => {
        return state.$session.get('user')?.companyUser?.subtype === 'ADMIN' ? true : false
    },
    isManager: state => {
        return state.$session.get('user')?.companyUser?.subtype === 'MANAGER' ? true : false
    },
    isOperator: state => {
        return state.$session.get('user')?.companyUser?.subtype === 'OPERATOR' ? true : false
    },
    getSubtype: state => {
        return state.$session.get('user')?.companyUser?.subtype ? state.$session.get('user')?.companyUser?.subtype : null
    },
    getAccountId: state => {
        const type = state.$session.get('user')?.account?.type
        return type === 'ACCOUNT' && state.$session.get('user')?.account ? state.$session.get('user')?.account?._id : ''
    },
    getClientId: state => {
        const type = state.$session.get('user')?.client?.type
        return type === 'CLIENT' && state.$session.get('user')?.client ? state.$session.get('user')?.client?._id : ''
    },
    getSiteGroupId: state => {
        const type = state.$session.get('user')?.client?.type
        return type === 'CLIENT' && state.$session.get('user') ? state.$session.get('user')?.siteGroup?.sites : ''
    },
    getLegacyAccount: async state => {
        const account = state.$session.get('user')?.account
        const { companyLegacyId, sqlLegacyBase } = account

        if (!companyLegacyId || !sqlLegacyBase) {
            return null
        }

        return {
            companyLegacyId,
            sqlLegacyBase,
        }
    },
    getEventStatusName(status) {
        switch (status) {
            case 'ACTIVE':
                return 'str.event.status.pendent'
            case 'CANCELED':
                return 'str.event.status.canceled'
            case 'DONE':
                return 'str.event.status.done'
            case 'EXPIRED':
                return 'str.event.status.expired'
            case 'FINISHED':
                return 'str.event.status.finished'
            case 'PENDENT':
                return 'str.event.status.pendent'
            case 'IN_PROGRESS':
                return 'str.event.status.in_progress'
            case 'CLOSED_BY_SYSTEM':
                return 'str.event.status.closed_by_system'
            case 'CANCELLED':
                return 'str.event.status.cancelled'

            default:
                return ''
        }
    },
    formatDateAndTime(date, utc = false) {
        if (!date) return ''
        if (utc) return moment.utc(date).format('DD/MM/YYYY - HH:mm:ss')
        return moment.tz(date, resolveAccountTimezone()).format('DD/MM/YYYY - HH:mm:ss')
    },
    normalizeTimezoneId: timezone => normalizeTimezoneId(timezone),
    resolveTimezoneId: timezone => resolveTimezoneId(timezone),
    buildTimezoneOption: timezone => buildTimezoneOption(timezone),
    formatGMTOffset: offsetInMinutes => formatGMTOffset(offsetInMinutes),
    getAccountTimezone: () => resolveAccountTimezone(),
    checkCPF: str => {
        /* eslint-disable */
        var re = /^\d{3}\.\d{3}\.\d{3}\-\d{2}$/
        return re.test(str)
    },
    checkCNPJ: str => {
        /* eslint-disable */
        var re = /^\d{2}\.\d{3}\.\d{3}\/\d{4}\-\d{2}$/
        return re.test(str)
    },
    getAccountLogoURL: async function (state, account) {
        if (account) {
            return account.logoURL ? `${domain}${account.logoURL}` : ''
        }

        const user = state.$session.get('user')
        return user?.account?.logoURL ? `${domain}${user.account.logoURL}` : ''
    },
}
</script>
